/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * HorizontalSubItems — horizontal scrollable chip strip (default style).
 *
 * Chips slide in from the right within the bar.
 * Long-press a chip to toggle favorite (star appears briefly).
 * Uses scroll-snap for better mobile UX.
 */

import { useState, useRef, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLayoutStore, useFavorites, useFavoritesDisplayStyle, FAVORITES_MAX } from '@/stores/layoutStore';
import type { BottomNavItem } from './types';

interface Props {
  items: BottomNavItem[];
  onNavigate: () => void;
}

export function HorizontalSubItems({ items, onNavigate }: Props) {
  const { t } = useTranslation('common');
  const favorites = useFavorites();
  const displayStyle = useFavoritesDisplayStyle();
  const addFavorite = useLayoutStore((s) => s.addFavorite);
  const removeFavorite = useLayoutStore((s) => s.removeFavorite);
  const isFull = favorites.length >= FAVORITES_MAX[displayStyle];

  const [longPressHref, setLongPressHref] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startLongPress = useCallback((href: string) => {
    timerRef.current = setTimeout(() => {
      setLongPressHref(href);
      const isFav = favorites.includes(href);
      if (isFav) removeFavorite(href);
      else if (!isFull) addFavorite(href);
      setTimeout(() => setLongPressHref(null), 800);
    }, 500);
  }, [favorites, isFull, addFavorite, removeFavorite]);

  const cancelLongPress = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return (
    <div className="bn-horizontal-strip">
      {items.map((item) => {
        const isFav = favorites.includes(item.href);
        const justToggled = longPressHref === item.href;
        return (
          <NavLink
            key={item.href}
            to={item.href}
            end
            className={({ isActive }) => cn('bn-chip', isActive && 'bn-chip-active')}
            onClick={onNavigate}
            onTouchStart={() => startLongPress(item.href)}
            onTouchEnd={cancelLongPress}
            onTouchCancel={cancelLongPress}
          >
            {justToggled ? (
              <Star size={16} fill={isFav ? 'none' : 'currentColor'} className="bn-chip-star-flash" />
            ) : (
              <item.icon size={16} />
            )}
            <span>{t(item.labelKey)}</span>
            {isFav && !justToggled && <Star size={10} fill="currentColor" className="bn-chip-fav-dot" />}
            {item.badge !== undefined && item.badge > 0 && (
              <span className={cn('bn-chip-badge', item.badgeVariant === 'danger' && 'danger')}>
                {item.badge}
              </span>
            )}
          </NavLink>
        );
      })}

      <style>{`
        .bn-chip-fav-dot {
          color: var(--color-warning);
          flex-shrink: 0;
          margin-left: -2px;
        }
        .bn-chip-star-flash {
          color: var(--color-warning);
          animation: bn-star-pop 0.3s ease both;
        }
        @keyframes bn-star-pop {
          0% { transform: scale(0.5); opacity: 0; }
          50% { transform: scale(1.3); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
